// Base
import { BaseApi } from 'api/base.api'

// Interface
import {
  ICertificationWebpageApiSelfAssessmentRequest,
  ICertificationWebpageSelfAssessmentApi,
  ICertificationWebpageSelfAssessmentApiListResponse,
  ICertificationWebpageSelfAssessmentApiGetListAttrs,
  ICertificationWebpageSelfAssessmentApiUploadDocumentAttrs,
  ICertificationWebpageSelfAssessmentApiDeleteDocumentAttrs,
  ICertificationWebpageSelfAssessmentApiGetDetailAttrs,
  ICertificationWebpageSelfAssessmentApiDetailResponse,
  ICertificationWebpageSelfAssessmentApiUpdateUserAnswerAttrs,
  ICertificationWebpageSelfAssessmentApiNullResponse,
  ICertificationWebpageSelfAssessmentApiUploadDocumentResponse,
  ICertificationWebpageSelfAssessmentApiDownloadDocumentAttrs,
  ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse,
} from 'interfaces/webpage/certification/registration/self-assessment/self-assessment-api.interface'

// Constant
import { ApiConstantHc, ApiConstantHcBase } from 'constant'

// Utils
import { axios_renderHcUrl } from 'utils'

export class CertificationSelfAssessmentWebpageApi
  extends BaseApi<ICertificationWebpageSelfAssessmentApi>
  implements ICertificationWebpageApiSelfAssessmentRequest
{
  readonly url = ApiConstantHc.CERTIFICATION_REGISTRATION_SELF_ASSESSMENT_URL
  private certificationRegistrationSelfAssessment = axios_renderHcUrl(this.url, ApiConstantHcBase.ASSESSMENT)

  /**
   * @description Get certification self assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiGetListAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiListResponse>} Promise<ICertificationWebpageSelfAssessmentApiListResponse>
   */
  async certificationWebpageSelfAssessment_getList(
    payload: ICertificationWebpageSelfAssessmentApiGetListAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiListResponse> {
    return this.POST(
      `${this.certificationRegistrationSelfAssessment}/list/${payload.params.assessmentId}`,
      {},
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Get certification self assessment detail
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiGetDetailAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiDetailResponse>} Promise<ICertificationWebpageSelfAssessmentApiDetailResponse>
   */
  async certificationWebpageSelfAssessment_getDetail(
    payload: ICertificationWebpageSelfAssessmentApiGetDetailAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiDetailResponse> {
    return this.POST(`${this.certificationRegistrationSelfAssessment}/${payload.params.assessmentId}`, payload.body, {
      ...payload.requestConfig,
    })
  }

  /**
   * @description Update self assessment user answer
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiUpdateUserAnswerAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiNullResponse>} Promise<ICertificationWebpageSelfAssessmentApiNullResponse>
   */
  async certificationWebpageSelfAssessment_updateUserAnswer(
    payload: ICertificationWebpageSelfAssessmentApiUpdateUserAnswerAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiNullResponse> {
    return this.POST(
      `${this.certificationRegistrationSelfAssessment}/updateUserAnswer/${payload.params.assessmentId}`,
      payload.body,
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Upload self assessment document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiUploadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiUploadDocumentResponse>} Promise<ICertificationWebpageSelfAssessmentApiUploadDocumentResponse>
   */
  async certificationWebpageSelfAssessment_uploadDocument(
    payload: ICertificationWebpageSelfAssessmentApiUploadDocumentAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiUploadDocumentResponse> {
    const formData = new FormData()
    formData.append('document', payload.body.document)
    formData.append('answerCode', payload.body.answerCode)

    if (payload.body.transactionFileId) formData.append('transactionFileId', payload.body.transactionFileId.toString())

    return this.POST(`${this.certificationRegistrationSelfAssessment}/uploadDocument/${payload.params.assessmentId}`, formData, {
      ...payload.requestConfig,
      headers: { 'Content-Type': 'multipart/form-data' },
    })
  }

  /**
   * @description Delete self assessment document
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiDeleteDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiNullResponse>} Promise<ICertificationWebpageSelfAssessmentApiNullResponse>
   */
  async certificationWebpageSelfAssessment_deleteDocument(
    payload: ICertificationWebpageSelfAssessmentApiDeleteDocumentAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiNullResponse> {
    return this.POST(
      `${this.certificationRegistrationSelfAssessment}/deleteDocument/${payload.params.userFileTransactionId}`,
      {},
      { ...payload.requestConfig },
    )
  }

  /**
   * @description Download document of self assessment
   *
   * @method POST
   * @access private (only user that logged in)
   *
   * @param {ICertificationWebpageSelfAssessmentApiDownloadDocumentAttrs} payload
   *
   * @return {Promise<ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse>} Promise<ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse>
   */
  async certificationWebpageSelfAssessment_downloadDocument(
    payload: ICertificationWebpageSelfAssessmentApiDownloadDocumentAttrs,
  ): Promise<ICertificationWebpageSelfAssessmentApiDownloadDocumentResponse> {
    return this.POST(
      `${this.certificationRegistrationSelfAssessment}/download/${payload.params.blobId}`,
      {},
      { ...payload.requestConfig, responseType: 'blob' },
    )
  }
}
